import {
  countConditions,
  emptyFilter,
  operatorNeedsValue,
  OPERATOR_LABELS,
  OPERATORS_BY_KIND,
  type FieldDescriptor,
  type FilterCondition,
  type FilterGroup,
  type FilterOperator,
  type FilterValue,
} from '@shared/filters';
import { Filter, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/feedback';
import { Input, Select } from '../../components/ui/form';

export interface FilterBarProps {
  fields: FieldDescriptor[];
  value: FilterGroup;
  onChange: (next: FilterGroup) => void;
}

/** Field ids are grouped in the picker exactly as the server groups them. */
function groupFields(fields: readonly FieldDescriptor[]): Array<{ group: string; fields: FieldDescriptor[] }> {
  const byGroup = new Map<string, FieldDescriptor[]>();
  for (const field of fields) {
    const list = byGroup.get(field.group) ?? [];
    list.push(field);
    byGroup.set(field.group, list);
  }
  return [...byGroup.entries()].map(([group, list]) => ({ group, fields: list }));
}

/** Coerce the typed input back into the shape the filter language expects for that field kind. */
function toFilterValue(raw: string, kind: FieldDescriptor['kind'], op: FilterOperator): FilterValue {
  if (op === 'in' || op === 'not_in') {
    return raw
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (kind === 'number') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : raw;
  }
  return raw;
}

function displayValue(value: FilterValue | undefined): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Structured filter builder over the server's whitelisted fields.
 *
 * Only fields the API advertises can be filtered, and the operators offered are the ones that
 * field's type actually supports — so a filter built here is always one the server will accept.
 */
export function FilterBar({ fields, value, onChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const grouped = useMemo(() => groupFields(fields), [fields]);
  const byId = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);
  const count = countConditions(value);

  const update = (index: number, patch: Partial<FilterCondition>): void => {
    const conditions = value.conditions.map((condition, position) =>
      position === index ? { ...condition, ...patch } : condition,
    );
    onChange({ ...value, conditions });
  };

  const remove = (index: number): void => {
    onChange({ ...value, conditions: value.conditions.filter((_, position) => position !== index) });
  };

  const add = (): void => {
    const first = fields[0];
    if (!first) return;
    const op = OPERATORS_BY_KIND[first.kind][0] ?? 'contains';
    onChange({ ...value, conditions: [...value.conditions, { field: first.id, op }] });
    setOpen(true);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={count > 0 ? 'accent' : 'outline'}
          icon={<Filter className="h-3.5 w-3.5" />}
          onClick={() => setOpen((current) => !current)}
        >
          Filters
          {count > 0 ? <Badge tone="neutral">{count}</Badge> : null}
        </Button>
        {count > 0 ? (
          <Button size="sm" variant="ghost" icon={<X className="h-3.5 w-3.5" />} onClick={() => onChange(emptyFilter())}>
            Clear all
          </Button>
        ) : null}
      </div>

      {open ? (
        <div className="space-y-2 rounded-[var(--radius-panel)] border border-border bg-surface p-3">
          {value.conditions.length > 1 ? (
            <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
              Match
              <Select
                className="h-7 w-24"
                value={value.conjunction}
                onChange={(event) => onChange({ ...value, conjunction: event.target.value as 'and' | 'or' })}
              >
                <option value="and">all of</option>
                <option value="or">any of</option>
              </Select>
              these conditions
            </label>
          ) : null}

          {value.conditions.map((condition, index) => {
            const field = byId.get(condition.field);
            const kind = field?.kind ?? 'text';
            const operators = OPERATORS_BY_KIND[kind];
            const needsValue = operatorNeedsValue(condition.op);

            return (
              <div key={`${condition.field}-${String(index)}`} className="flex flex-wrap items-center gap-2">
                <Select
                  className="h-8 w-52 text-[13px]"
                  aria-label="Field"
                  value={condition.field}
                  onChange={(event) => {
                    const nextField = byId.get(event.target.value);
                    const nextOp = nextField ? (OPERATORS_BY_KIND[nextField.kind][0] ?? 'contains') : condition.op;
                    update(index, { field: event.target.value, op: nextOp, value: undefined, value2: undefined });
                  }}
                >
                  {grouped.map((entry) => (
                    <optgroup key={entry.group} label={entry.group}>
                      {entry.fields.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>

                <Select
                  className="h-8 w-40 text-[13px]"
                  aria-label="Condition"
                  value={condition.op}
                  onChange={(event) => update(index, { op: event.target.value as FilterOperator, value: undefined, value2: undefined })}
                >
                  {operators.map((op) => (
                    <option key={op} value={op}>
                      {OPERATOR_LABELS[op]}
                    </option>
                  ))}
                </Select>

                {needsValue ? (
                  field?.kind === 'enum' && field.options && condition.op !== 'in' && condition.op !== 'not_in' ? (
                    <Select
                      className="h-8 w-44 text-[13px]"
                      aria-label="Value"
                      value={displayValue(condition.value)}
                      onChange={(event) => update(index, { value: event.target.value })}
                    >
                      <option value="">Choose…</option>
                      {field.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      className="h-8 w-44 text-[13px]"
                      aria-label="Value"
                      type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
                      placeholder={condition.op === 'in' || condition.op === 'not_in' ? 'a, b, c' : 'Value'}
                      value={displayValue(condition.value)}
                      onChange={(event) => update(index, { value: toFilterValue(event.target.value, kind, condition.op) })}
                    />
                  )
                ) : null}

                {condition.op === 'between' ? (
                  <Input
                    className="h-8 w-32 text-[13px]"
                    aria-label="Upper value"
                    type={kind === 'date' ? 'date' : 'number'}
                    placeholder="and"
                    value={condition.value2 === undefined ? '' : String(condition.value2)}
                    onChange={(event) =>
                      update(index, { value2: kind === 'number' ? Number(event.target.value) : event.target.value })
                    }
                  />
                ) : null}

                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Remove this condition"
                  onClick={() => remove(index)}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            );
          })}

          <Button size="sm" variant="outline" icon={<Plus className="h-3.5 w-3.5" />} onClick={add}>
            Add condition
          </Button>
        </div>
      ) : null}
    </div>
  );
}
